import clsx from 'clsx'

/**
 * Tag color options.
 * - Filled: Black, Blue, Red, Neutral (white text)
 * - Tonal: Black, Blue, Red, Neutral, Green, Yellow, Orange (colored text on light background)
 */
export type TagColor = 'Black' | 'Blue' | 'Red' | 'Neutral' | 'Green' | 'Yellow' | 'Orange'

export type TagSize = '2xs' | 'xs'

export type TagVariant = 'filled' | 'tonal'

export type TagTextCase = 'all-caps' | 'normal'

export type TagShape = 'square' | 'pill'

export interface TagProps {
  /** Tag content */
  children: React.ReactNode
  /** Visual variant: filled (solid bg + white text) or tonal (light bg + border + colored text) */
  variant?: TagVariant
  /** Color. Filled supports Black, Blue, Red, Neutral. Tonal supports all. */
  color?: TagColor
  /** Size: 2xs or xs. Default is 2xs. */
  size?: TagSize
  /** Text case: all-caps status label (default) or normal case for a metadata tag. */
  textCase?: TagTextCase
  /** Corner shape: 4px radius (default) or a fully rounded pill. */
  shape?: TagShape
  /** Whether the first child is a leading icon. Reduces left padding by 2px to balance the icon's own visual weight. */
  hasLeadingIcon?: boolean
}

const filledStyles: Record<Extract<TagColor, 'Black' | 'Blue' | 'Red' | 'Neutral'>, string> = {
  Black: 'bg-tag-filled-black-bg text-tag-filled-black-text',
  Blue: 'bg-tag-filled-blue-bg text-tag-filled-blue-text',
  Red: 'bg-tag-filled-red-bg text-tag-filled-red-text',
  Neutral: 'bg-tag-filled-neutral-bg text-tag-filled-neutral-text',
}

const tonalStyles: Record<TagColor, string> = {
  Black: 'bg-tag-tonal-black-bg ring-1 ring-inset ring-tag-tonal-black-border text-tag-tonal-black-text',
  Neutral: 'bg-tag-tonal-neutral-bg ring-1 ring-inset ring-tag-tonal-neutral-border text-tag-tonal-neutral-text',
  Blue: 'bg-tag-tonal-blue-bg ring-1 ring-inset ring-tag-tonal-blue-border text-tag-tonal-blue-text',
  Red: 'bg-tag-tonal-red-bg ring-1 ring-inset ring-tag-tonal-red-border text-tag-tonal-red-text',
  Green: 'bg-tag-tonal-green-bg ring-1 ring-inset ring-tag-tonal-green-border text-tag-tonal-green-text',
  Yellow: 'bg-tag-tonal-yellow-bg ring-1 ring-inset ring-tag-tonal-yellow-border', // text is size-dependent, see yellowTonalTextBySize
  Orange: 'bg-tag-tonal-orange-bg ring-1 ring-inset ring-tag-tonal-orange-border text-tag-tonal-orange-text',
}

const yellowTonalTextBySize: Record<TagSize, string> = {
  '2xs': 'text-tag-tonal-yellow-text-2xs',
  xs: 'text-tag-tonal-yellow-text-xs',
}

// DM Sans has no true small-caps glyphs, so the status chip uses real uppercase text instead.
// Symmetric top/bottom padding keeps text centered in the pill; min-h backstops the total
// height since padding + line-height alone don't reach it.
const sizeStyles: Record<TagSize, Record<TagTextCase, string>> = {
  '2xs': {
    'all-caps': 'min-h-[14px] pt-[1px] leading-[10px] text-[8.5px] font-semibold tracking-[0.5px] uppercase',
    normal: 'min-h-[14px] pt-[1px] leading-[10.4px] text-[10.4px] font-medium',
  },
  xs: {
    'all-caps': 'min-h-[18px] py-[2px] leading-[9px] text-[10px] font-semibold tracking-[0.5px] uppercase',
    normal: 'min-h-[18px] py-[2px] leading-[10px] text-[12px] font-medium',
  },
}

const FILLED_COLORS: TagColor[] = ['Black', 'Blue', 'Red', 'Neutral']

// withLeadingIcon variants trim 2px off the left padding only, to balance a leading icon's own visual weight.
const shapeStyles: Record<TagShape, { base: string; withLeadingIcon: string }> = {
  square: { base: 'rounded px-1', withLeadingIcon: 'rounded pl-0.5 pr-1' },
  pill: { base: 'rounded-full px-1.5', withLeadingIcon: 'rounded-full pl-1 pr-1.5' },
}

/**
 * Tag component
 * Supports filled (solid background) and tonal (light background + border) variants.
 */
export function Tag({
  children,
  variant = 'tonal',
  color = 'Neutral',
  size = '2xs',
  textCase = 'all-caps',
  shape = 'square',
  hasLeadingIcon = false,
}: TagProps) {
  const resolvedColor = variant === 'filled' && !FILLED_COLORS.includes(color) ? 'Neutral' : color
  const styleClasses = variant === 'filled' ? filledStyles[resolvedColor as keyof typeof filledStyles] : tonalStyles[resolvedColor]
  const yellowText = variant === 'tonal' && resolvedColor === 'Yellow' ? yellowTonalTextBySize[size] : null

  return (
    <span
      data-ds-component="Tag"
      data-ds-variant={variant}
      data-ds-color={resolvedColor}
      data-ds-size={size}
      data-ds-text-case={textCase}
      data-ds-shape={shape}
      className={clsx(
        // gap only shows when a caller passes a leading icon alongside the label
        'inline-flex items-center justify-center gap-0.5 whitespace-nowrap shrink-0',
        hasLeadingIcon ? shapeStyles[shape].withLeadingIcon : shapeStyles[shape].base,
        styleClasses,
        yellowText,
        sizeStyles[size][textCase]
      )}
    >
      {children}
    </span>
  )
}
